import React, { CSSProperties, forwardRef, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { OverlayContainer, useId, usePreventScroll } from 'react-aria';
import { isDesktop, isMobile } from '@direct-frontend/platform-code';
import { getPlatformStyles } from '@direct-frontend/platform-code';
import { genericMemo, mergeRefs } from '@direct-frontend/stdlib';
import { LoggableProps, useLogger } from '@direct-frontend/modules-logger';

import { cn } from '../../lib/css/className/className';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useOverlay } from '../../hooks/useOverlay/useOverlay';
import { useBackButton } from '../../hooks/useBackButton/useBackButton';
import { useInvertedTheme } from '../../lib/theme';
import { IconCloseSkinny as IconClose } from '../../icons/colorless';
import { ClickableIcon } from '../ClickableIcon';
import { TestProps } from '../../internal/types/testTypes';
import { GestureEvents } from '../../internal/types/gestureTypes';
import {
    LayerContextProvider,
    LayerPriorities,
    LayerPriorityType,
    useNewLayer,
} from '../../lib/layerManager/layerManager';
import { LayerFocusScope } from '../../lib/layerManager/components/LayerFocusScope/LayerFocusScope';

import { useMobileViewportOverlayResize } from './hooks/useMobileViewportOverlayResize/useMobileViewportOverlayResize';
import { i18n } from './Overlay.i18n';

import './Overlay.css';

/**
 * Компонент, подключающий содержимое Overlay к useLogger (baobab)
 *
 * В данный компонент необходимо оборачивать children Overlay, чтобы при логировании учитывалось состояние isOpen
 */
export const OverlayContentLogProvider = function OverlayContentLogProvider(
    props: LoggableProps<{ children: ReactNode; componentName: string }>,
) {
    const { LogProvider } = useLogger(props.componentName, props.logNode);

    return <LogProvider>{props.children}</LogProvider>;
};

getPlatformStyles({
    common: import('./Overlay.css'),
    desktop: import('./Overlay@desktop.css'),
});

const cls = cn('Overlay');

export interface OverlayBackDismissProps {
    /**
     * Закрывать ли компонент при нажатии на системную клавишу Back.
     * Работает только на мобиле, имеет ряд проблем, подробнее в readme компонента Overlay.
     */
    dismissOnBack?: boolean;
}

export interface OverlayProps extends TestProps, GestureEvents, OverlayBackDismissProps {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Дочерние элементы
     * Кнопка-крестик уже встроена
     */
    children?: ReactNode;

    /**
     * Ссылка на контейнер для контента
     */
    innerContentRef?: React.RefObject<HTMLDivElement>;

    /**
     * Коллбек, вызываемый при завершении анимации
     */
    onAnimationEnd?: (event: React.AnimationEvent<HTMLElement>) => void;

    /**
     * Переключение между открытым и закрытым состояниями
     * @default false
     */
    isOpen?: boolean;

    /**
     * Действие, выполняемое при закрытии
     */
    onClose?: () => void;

    /**
     * Действие, выполняемое при отклоненном закрытии
     */
    onCloseCanceled?: () => void;

    /**
     * Требуется ли закрывать компонент при клике на затемнённый фон (overlay)
     * @default true
     */
    dismissOnOutsideClick?: boolean;

    /**
     * Требуется ли закрывать компонент при клике на клавишу `Esc`
     * @default true
     */
    dismissOnEsc?: boolean;

    /**
     * Используется ли кнопка-крестик для закрытия компонента
     * @default false
     */
    hasCloseButton?: boolean;

    /**
     * Запрещает прокрутку страницы, пока открыт компонент Overlay
     * @default true
     */
    preventScroll?: boolean;

    /**
     * Компонент для закрытия Overlay.
     * Данный параметр позволяет использовать альтернативный элемент для выхода
     * @default <CloseButton />
     */
    closeButton?: React.ReactElement<{ onClick: () => void }>;

    /**
     * Контейнер, в который нужно поместить портал для Overlay
     * @default document.body
     */
    portalContainer?: HTMLElement;

    /**
     * Названия классов для кнопки-крестика
     */
    closeButtonClassName?: string;

    /**
     * Ограничевает height в 100% viewport'а у контента модалки
     * (может пригодиться если в содержимом хотите добавлять скролл при overflow,
     * также используется в Modal со scrollable=true)
     * @default true
     */
    limitHeightByViewport?: boolean;

    /**
     * Позиция контента Overlay
     * @default top
     */
    position?: 'top' | 'center';

    /**
     * Растянуть контент Overlay на всю доступную высоту viewport (только для desktop).
     */
    height?: 'max';

    /**
     * Нужно ли удерживать фокус внутри оверлея.
     * Используется как временное решение, пока в DNA из библиотечных модалок открываются старые
     * @default true
     * @deprecated
     */
    containFocus?: boolean;

    /**
     * Фокусировать первый доступный элемент, когда оверлей становится верхним слоем.
     * @default true
     */
    autoFocus?: boolean;

    /**
     * Приоритет слоя, определяет как будут высчитываться его zIndex
     *
     * @default Priorities.Default
     */
    priority?: LayerPriorityType;
}

const overlayCloseButtonNode = { name: 'OverlayCloseButton' };

const CloseButton = (props: { onClick?: () => void; className?: string; 'data-testid'?: string }) => {
    const closeButtonText = i18n('Закрыть');
    const invertedTheme = useInvertedTheme();

    return (
        <ClickableIcon
            className={cls('close-button', [invertedTheme, props.className])}
            onClick={props.onClick}
            color="gray"
            clickableAreaSize="68"
            title={closeButtonText}
            aria-label={closeButtonText}
            logNode={overlayCloseButtonNode}
            data-testid={`${props['data-testid'] ?? 'Overlay'}.CloseButton`}
        >
            <IconClose size="24" />
        </ClickableIcon>
    );
};

const scrollClass = getScrollbarClassName();

/**
 * Компонент для отображения затемнения фона
 */
export const Overlay = genericMemo(
    forwardRef<HTMLDivElement, OverlayProps>(function Overlay(
        {
            className,
            closeButtonClassName,
            children,
            innerContentRef,
            isOpen = false,
            onClose,
            onCloseCanceled,
            dismissOnOutsideClick = true,
            dismissOnEsc = true,
            dismissOnBack,
            hasCloseButton = false,
            preventScroll = true,
            portalContainer,
            closeButton,
            position = 'top',
            height,
            limitHeightByViewport = true,
            containFocus = true,
            autoFocus = true,
            priority = LayerPriorities.Default,
            onAnimationEnd,
            ...props
        },
        forwardedRef,
    ) {
        const overlayId = useId();
        const ref = React.useRef(null);
        const layerManager = useNewLayer({
            priority,
            contextPopupsContainerRef: ref,
            containerContentRef: innerContentRef,
        });

        const layerZIndex = layerManager.zIndex;

        const { overlayProps, underlayProps } = useOverlay(
            {
                isOpen,
                onClose,
                onCloseCanceled,
                isModal: true,
                isDismissable: dismissOnOutsideClick,
                isKeyboardDismissDisabled: !dismissOnEsc,
                isLayer: true,
                zIndex: layerZIndex,
                id: layerManager.id,
            },
            ref,
        );

        usePreventScroll({ isDisabled: !isOpen || !preventScroll });

        const customCloseButton = closeButton ? React.cloneElement(closeButton, { onClick: onClose }) : undefined;

        const [addToDom, setAddToDom] = useState(false);

        if (isOpen && !addToDom) {
            setAddToDom(true);
        }

        // управление работой анимаций (исчезновение элемента из DOM происходит по окончании анимации закрытия)
        const onCloseAnimationEnd = useCallback<React.AnimationEventHandler<HTMLElement>>(
            event => {
                if (!isOpen) {
                    setAddToDom(false);
                }

                onAnimationEnd?.(event);
            },
            [isOpen, onAnimationEnd],
        );

        const style = useMemo<CSSProperties>(() => ({ zIndex: layerZIndex }), [layerZIndex]);

        const { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onMouseUp, onMouseMove, onMouseDown } = props;
        const gestureEvents = {
            onTouchStart,
            onTouchMove,
            onTouchEnd,
            onTouchCancel,
            onMouseUp,
            onMouseMove,
            onMouseDown,
        };

        useBackButton(
            () => {
                if (dismissOnBack) {
                    onClose?.();
                }
            },
            { enabled: isMobile() && Boolean(isOpen && dismissOnBack) },
        );

        const localOverlayRef = useRef(null);
        const mergedRef = mergeRefs(forwardedRef, localOverlayRef);
        useMobileViewportOverlayResize(localOverlayRef);

        const autoFocusProps = useMemo(() => {
            return { contain: containFocus, restoreFocus: true, autoFocus };
        }, [containFocus, autoFocus]);

        if (!addToDom) {
            return null;
        }

        return (
            <OverlayContainer
                portalContainer={portalContainer}
                onAnimationEnd={onCloseAnimationEnd}
                className={cls('container')}
                style={style}
            >
                <LayerContextProvider value={layerManager}>
                    <div
                        id={overlayId}
                        ref={mergedRef}
                        data-testid={props['data-testid'] ?? 'Overlay'}
                        className={cls(
                            {
                                state: isOpen ? 'open' : 'close',
                                'limit-height': Boolean(limitHeightByViewport),
                                height: height === 'max' ? 'max' : undefined,
                            },
                            [className, scrollClass],
                        )}
                        {...underlayProps}
                        {...gestureEvents}
                    >
                        <LayerFocusScope autoFocusProps={autoFocusProps} isOpen={isOpen}>
                            <div
                                ref={ref}
                                {...overlayProps}
                                tabIndex={0}
                                className={cls('focus-ring-remover', { position })}
                                focus-fallback="true"
                            >
                                {hasCloseButton &&
                                    isDesktop() &&
                                    (customCloseButton || (
                                        <CloseButton
                                            onClick={onClose}
                                            className={closeButtonClassName}
                                            data-testid={props['data-testid']}
                                        />
                                    ))}
                                {children}
                            </div>
                        </LayerFocusScope>
                    </div>
                </LayerContextProvider>
            </OverlayContainer>
        );
    }),
);
